import React from 'react';
import './PlayerHeatmapCard.css';

const GOAL_WORD = /\b(scored|scores|netted|converted|goal)\b/i;
const ASSIST_WORD = /\bassist/i;
const NOT_A_GOAL = /\b(conceded|missed|chance|opportunity|saved|denied)\b/i;

// Counts real, already-observed keyActions — not invented totals — so the
// stat row under the card is just a readout of the same data the report
// already contains, in a glanceable form.
function countMatching(keyActions, re, exclude) {
  return (keyActions || []).filter(a => re.test(a) && !(exclude && exclude.test(a))).length;
}

// Full-bleed pitch card for a "top performer" — same honest zone estimates
// as PlayerHeatmap, rendered as soft blurred hotspots across a textured
// pitch background instead of a discrete grid, matching how broadcast
// heatmaps are usually drawn. Still an approximation, not tracked positions.
export default function PlayerHeatmapCard({ player, color = '#00d4ff', onClick }) {
  const p = player;
  const zones = p.zones;

  const goals = countMatching(p.keyActions, GOAL_WORD, NOT_A_GOAL);
  const assists = countMatching(p.keyActions, ASSIST_WORD);
  const passPct = p.stats?.passAccuracy;

  let blobs = [];
  if (zones) {
    const left = zones.leftFlank ?? 33, center = zones.center ?? 34, right = zones.rightFlank ?? 33;
    const own = zones.ownHalf ?? 33, final = zones.finalThird ?? 33;
    const mid = Math.max(0, 100 - own - final);
    const hSum = (left + center + right) || 1;
    const vSum = (own + mid + final) || 1;
    const hW = [left / hSum, center / hSum, right / hSum];
    const vW = [final / vSum, mid / vSum, own / vSum];
    const hPos = [20, 50, 80];
    const vPos = [22, 50, 80];
    let max = 0;
    const cells = [];
    for (let r = 0; r < 3; r++) {
      for (let c = 0; c < 3; c++) {
        const v = vW[r] * hW[c];
        cells.push({ top: vPos[r], left: hPos[c], v });
        if (v > max) max = v;
      }
    }
    blobs = cells.map(cell => ({ ...cell, intensity: max > 0 ? cell.v / max : 0 }));
  }

  return (
    <div className="phc" onClick={onClick}>
      <div className="phc__header">
        <div className="phc__avatar">{p.name?.[0] || '?'}</div>
        <div className="phc__name-block">
          <div className="phc__name">{p.name}</div>
          <div className="phc__meta">{p.teamName} · {p.position}</div>
        </div>
        <div className="phc__rating" data-h={p.rating >= 8 ? 'high' : p.rating >= 6 ? 'mid' : 'low'}>{p.rating?.toFixed(1)}</div>
      </div>

      <div className="phc__pitch">
        <svg viewBox="0 0 100 130" className="phc__pitch-lines" preserveAspectRatio="none">
          <rect x="2" y="2" width="96" height="126" rx="2" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="0.6" />
          <line x1="2" y1="65" x2="98" y2="65" stroke="rgba(255,255,255,0.2)" strokeWidth="0.5" />
          <circle cx="50" cy="65" r="10" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="0.5" />
          <rect x="22" y="2" width="56" height="16" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="0.5" />
          <rect x="22" y="112" width="56" height="16" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="0.5" />
        </svg>
        {blobs.map((b, i) => (
          <div
            key={i}
            className="phc__blob"
            style={{
              top: `${b.top}%`, left: `${b.left}%`,
              opacity: 0.15 + 0.65 * b.intensity,
              background: color,
            }}
          />
        ))}
        {!zones && <div className="phc__no-zones">Positional data not available</div>}
      </div>

      <div className="phc__stats">
        <div className="phc__stat"><div className="phc__stat-val">{goals}</div><div className="phc__stat-key">Goals</div></div>
        <div className="phc__stat"><div className="phc__stat-val">{assists}</div><div className="phc__stat-key">Assists</div></div>
        <div className="phc__stat"><div className="phc__stat-val">{passPct != null ? `${passPct}%` : '—'}</div><div className="phc__stat-key">Pass %</div></div>
      </div>
    </div>
  );
}
